/* eslint-disable */
/**
 * buildCorePlanPayload
 *
 * Turns the flat dotted-path `formData` produced by DynamicPlanFields + CoreConfig
 * into the nested plan payload the backend expects.
 *
 * The structural half (coverage_for, benefit_amount, age_criteria,
 * dependent_eligibility, rider, …) mirrors CreateVEBPlanV2's payload builder
 * one-for-one, so a Core plan saved with `same_as_veb_structure: true` is shaped
 * like a VEB plan. On top of that it carries the Core-only IPlanSchema keys
 * (has_premium, is_dependent_applied, buy_up_eligibility, is_always_open,
 * plan_dependencies, …). internal_dependencies is always empty in this mode.
 *
 * NOTE: as of this change the backend Core plan model
 * (node_server_typescript/src/models/plan/plan.model.ts) does not declare the VEB
 * fields, so Mongoose will strip them until that schema is extended.
 */

// ── Shared helpers (same semantics as CreateVEBPlanV2) ───────────────────────

export const formatToTitleCase = (value: string): string => {
	if (!value) return "";

	return value
		.trim()
		.replace(/\s+/g, " ")
		.toLowerCase()
		.replace(/\b\w/g, (char) => char.toUpperCase());
};

export const trimSpaces = (value: string): string => String(value ?? "").trim();

export const coverageSort = (coverageArray: string[]): string[] => {
	if (!Array.isArray(coverageArray)) return [];

	const order = [
		"Employee Only",
		"Spouse Only",
		"Children Only",
		"Employee & Spouse",
		"Employee & Children",
		"Employee & Family"
	];

	return [...coverageArray].sort((a, b) => {
		const indexA = order.indexOf(a);
		const indexB = order.indexOf(b);

		if (indexA === -1 && indexB === -1) return a.localeCompare(b);
		if (indexA === -1) return 1;
		if (indexB === -1) return -1;

		return indexA - indexB;
	});
};

export const generateBenefitOptions = (min: number, max: number, step: number): number[] => {
	const _min = Number(min);
	const _max = Number(max);
	const _step = Number(step);

	// Guard against an infinite loop / invalid range.
	if (!_step || _step <= 0 || isNaN(_min) || isNaN(_max) || isNaN(_step) || _min > _max) {
		return [];
	}

	const options: number[] = [];
	for (let i = _min; i <= _max; i += _step) {
		options.push(i);
	}
	return options;
};

export const generateSpecificAgeListOptions = (min: number, max: number): number[] => {
	const _min = Number(min);
	const _max = Number(max);
	if (isNaN(_min) || isNaN(_max) || _min > _max) return [];

	const options: number[] = [];
	for (let i = _min; i <= _max; i++) options.push(i);
	return options;
};

type Fd = Record<string, any>;

/** Benefit block shared by employee / spouse / child / spouse_and_child. */
const buildAgeCriteria = (fd: Fd, prefix: string, specificAges: number[]) => ({
	is_present: fd[`${prefix}.enable_specific_ages`] || fd[`${prefix}.use_age_bands`] ? true : false,

	use_specific_ages: fd[`${prefix}.enable_specific_ages`] ? true : false,

	min_age: fd[`${prefix}.enable_specific_ages`] ? Number(fd[`${prefix}.min_age`]) : 0,

	max_age: fd[`${prefix}.enable_specific_ages`] ? Number(fd[`${prefix}.max_age`]) : 0,

	age_step: 1,

	specific_age_list: fd[`${prefix}.enable_specific_ages`] ? specificAges : [],

	use_age_bands: fd[`${prefix}.premium_depend_on_age`] && fd[`${prefix}.use_age_bands`] ? true : false,

	age_band_ranges:
		fd[`${prefix}.premium_depend_on_age`] && fd[`${prefix}.use_age_bands`]
			? (fd[`${prefix}.age_band_ranges`] || []).map((age: any) => trimSpaces(String(age)) || 0)
			: []
});

/** Guaranteed-issue / simplified-issue block, identical at every level. */
const buildIssueBlock = (fd: Fd, prefix: string) => {
	const p = prefix ? `${prefix}.` : "";
	return {
		has_guaranteed_issue: fd[`${p}has_guaranteed_issue`] || false,

		guaranteed_issue_amount: fd[`${p}has_guaranteed_issue`]
			? Number(fd[`${p}guaranteed_issue_amount`]) || null
			: null,

		requires_eoi_above_gi: false,

		has_simplified_issue: fd[`${p}has_simplified_issue`] || false,

		simplified_issue_amount: fd[`${p}has_simplified_issue`]
			? Number(fd[`${p}simplified_issue_amount`]) || null
			: null,

		requires_eoi_above_si: false,

		has_si_questions: fd[`${p}has_simplified_issue`] && fd[`${p}has_si_questions`] ? true : false,

		si_questions: fd[`${p}has_si_questions`] ? fd[`${p}si_questions`] || [] : []
	};
};

/** Per-relation (spouse / child) benefit block. */
const buildRelationBenefit = (fd: Fd, prefix: string, relation: "spouse" | "child") => {
	const predefined = generateBenefitOptions(
		Number(fd[`${prefix}.min_benefit`]),
		Number(fd[`${prefix}.max_benefit`]),
		Number(fd[`${prefix}.each_benefit_multiply_by`])
	);

	const specificAges = generateSpecificAgeListOptions(Number(fd[`${prefix}.min_age`]), Number(fd[`${prefix}.max_age`]));

	const enrollKey =
		relation === "spouse" ? "spouse_can_enroll_individually" : "child_can_enroll_individually";
	const exceedKey =
		relation === "spouse" ? "can_spouse_benefit_exceed_employee" : "can_child_benefit_exceed_employee";

	return {
		[enrollKey]: fd[`${prefix}.${enrollKey}`] || false,

		min_benefit: fd[`${prefix}.use_predefine_benefit`] ? Number(fd[`${prefix}.min_benefit`]) || 0 : 0,

		each_benefit_multiply_by: fd[`${prefix}.use_predefine_benefit`]
			? Number(fd[`${prefix}.each_benefit_multiply_by`]) || 0
			: 0,

		max_benefit: fd[`${prefix}.use_predefine_benefit`] ? Number(fd[`${prefix}.max_benefit`]) || 0 : 0,

		options: fd[`${prefix}.enable_specific_benefit`]
			? (fd[`${prefix}.options`] || []).map((x: any) => Number(x))
			: fd[`${prefix}.use_predefine_benefit`]
			? predefined.map(Number)
			: [],

		is_benefit_based_on_salary: fd[`${prefix}.is_benefit_based_on_salary`] || false,

		salary_multiplier: fd[`${prefix}.is_benefit_based_on_salary`]
			? Number(fd[`${prefix}.salary_multiplier`]) || null
			: null,

		salary_multiplier_max_cap: fd[`${prefix}.is_benefit_based_on_salary`]
			? Number(fd[`${prefix}.salary_multiplier_max_cap`]) || null
			: null,

		[exceedKey]: fd[`${prefix}.${exceedKey}`] || false,

		age_criteria: buildAgeCriteria(fd, prefix, specificAges),

		...buildIssueBlock(fd, prefix)
	};
};

/** Combined spouse-and-child benefit block. */
const buildSpouseAndChildBenefit = (fd: Fd, prefix: string) => {
	const combined = generateBenefitOptions(
		Number(fd[`${prefix}.min_benefit`]),
		Number(fd[`${prefix}.max_benefit`]),
		Number(fd[`${prefix}.each_benefit_multiply_by`])
	);

	return {
		min_benefit: fd[`${prefix}.enable_specific_benefit`] ? Number(fd[`${prefix}.min_benefit`]) || 0 : 0,

		each_benefit_multiply_by: fd[`${prefix}.enable_specific_benefit`]
			? Number(fd[`${prefix}.each_benefit_multiply_by`]) || 0
			: 0,

		max_benefit: fd[`${prefix}.enable_specific_benefit`] ? Number(fd[`${prefix}.max_benefit`]) || 0 : 0,

		benefit_amount_round_to_nearest: Number(fd[`${prefix}.benefit_amount_round_to_nearest`]) || null,

		options: fd[`${prefix}.enable_specific_benefit`]
			? combined.map(Number)
			: fd[`${prefix}.use_predefine_benefit`]
			? (fd[`${prefix}.benefit_band_ranges`] || []).map((x: any) => Number(x))
			: [],

		use_employee_benefit: fd[`${prefix}.use_employee_benefit`] || false,
		use_spouse_benefit: fd[`${prefix}.use_spouse_benefit`] || false,
		use_child_benefit: fd[`${prefix}.use_child_benefit`] || false,
		use_separate_benefit: fd[`${prefix}.use_separate_benefit`] || fd[`${prefix}.use_seperate_benefit`] || false
	};
};

/**
 * Employee-level benefit_amount block.
 * `prefix` is "benefit_amount" for the plan, "rider.benefit_amount" for a rider.
 * `isBenefitKey` is the flag that gates it ("is_benefit" / "rider.is_benefit").
 */
const buildBenefitAmount = (fd: Fd, prefix: string, isBenefitKey: string) => {
	const isBenefit = fd[isBenefitKey];

	const benefitOptions = generateBenefitOptions(
		fd[`${prefix}.min_benefit`],
		fd[`${prefix}.max_benefit`],
		fd[`${prefix}.each_benefit_multiply_by`]
	);

	return {
		is_present: isBenefit || false,
		is_visible_in_form: false,

		min_benefit: isBenefit && fd[`${prefix}.enable_specific_benefit`] ? Number(fd[`${prefix}.min_benefit`]) || 0 : 0,

		max_benefit: isBenefit && fd[`${prefix}.enable_specific_benefit`] ? Number(fd[`${prefix}.max_benefit`]) || 0 : 0,

		each_benefit_multiply_by:
			isBenefit && fd[`${prefix}.enable_specific_benefit`]
				? Number(fd[`${prefix}.each_benefit_multiply_by`]) || 0
				: 0,

		options: !isBenefit
			? []
			: fd[`${prefix}.enable_specific_benefit`]
			? benefitOptions.map(Number)
			: fd[`${prefix}.use_predefine_benefit`]
			? (fd[`${prefix}.benefit_band_ranges`] || []).map((x: any) => Number(x))
			: [],

		benefit_amount_round_to_nearest: Number(fd[`${prefix}.benefit_amount_round_to_nearest`]) || null,

		is_each_member_has_different_benefit_amount:
			fd[`${prefix}.is_each_member_has_different_benefit_amount`] || false,

		// ── Salary-derived benefit ──
		is_benefit_based_on_salary: fd[`${prefix}.is_benefit_based_on_salary`] || false,

		is_benefit_amount_based_on_yearly_salary_percentage:
			fd[`${prefix}.is_benefit_amount_based_on_yearly_salary_percentage`] || false,

		salary_percentage: fd[`${prefix}.is_benefit_amount_based_on_yearly_salary_percentage`]
			? Number(fd[`${prefix}.salary_percentage`]) || null
			: null,

		is_benefit_amount_based_on_yearly_salary_multiplier:
			fd[`${prefix}.is_benefit_amount_based_on_yearly_salary_multiplier`] || false,

		yearly_salary_multiplier: fd[`${prefix}.is_benefit_amount_based_on_yearly_salary_multiplier`]
			? Number(fd[`${prefix}.yearly_salary_multiplier`]) || null
			: null,

		salary_multiplier_max_cap: fd[`${prefix}.is_benefit_amount_based_on_yearly_salary_multiplier`]
			? Number(fd[`${prefix}.salary_multiplier_max_cap`]) || null
			: null,

		spouse: buildRelationBenefit(fd, `${prefix}.spouse`, "spouse"),
		child: buildRelationBenefit(fd, `${prefix}.child`, "child"),
		spouse_and_child: buildSpouseAndChildBenefit(fd, `${prefix}.spouse_and_child`)
	};
};

/** Plan- or rider-level age_criteria block. */
const buildPlanAgeCriteria = (fd: Fd, prefix: string, gateKey: string) => {
	const gated = fd[gateKey];
	const specificAgeList = generateSpecificAgeListOptions(fd[`${prefix}.min_age`], fd[`${prefix}.max_age`]);

	return {
		is_present: gated || false,
		is_visible_in_form: false,

		use_specific_ages: gated && fd[`${prefix}.enable_specific_ages`] ? true : false,

		min_age: gated && fd[`${prefix}.enable_specific_ages`] ? Number(fd[`${prefix}.min_age`]) : 0,

		max_age: gated && fd[`${prefix}.enable_specific_ages`] ? Number(fd[`${prefix}.max_age`]) : 0,

		age_step: 1,

		specific_age_list: gated && fd[`${prefix}.enable_specific_ages`] ? specificAgeList : [],

		use_age_bands: gated && fd[`${prefix}.use_age_bands`] ? true : false,

		age_band_ranges:
			gated && fd[`${prefix}.use_age_bands`]
				? (fd[`${prefix}.age_band_ranges`] || []).map((age: any) => trimSpaces(String(age)) || 0)
				: []
	};
};

/** Plan- or rider-level dependent_eligibility block. */
const buildDependentEligibility = (fd: Fd, prefix: string, gateKey: string) => ({
	is_present: !!fd[gateKey],
	is_visible_in_form: false,
	is_each_relationship_has_diffrent_premium: fd["has_dependent_eligibility"] || false,

	eligible_relationship: Array.isArray(fd[`${prefix}.eligible_relationship`])
		? fd[`${prefix}.eligible_relationship`]
		: [],

	in_eligible_relationship: Array.isArray(fd[`${prefix}.in_eligible_relationship`])
		? fd[`${prefix}.in_eligible_relationship`]
		: [],

	spouse: {
		min_age: Number(fd[`${prefix}.spouse.min_age`]) || 18,
		max_age: Number(fd[`${prefix}.spouse.max_age`]) || 100,
		can_spouse_has_max_benefit_amount_than_employee:
			!!fd[`${prefix}.spouse.can_spouse_has_max_benefit_amount_than_employee`]
	},

	children: {
		min_age: Number(fd[`${prefix}.children.min_age`]) || 0,
		max_age: Number(fd[`${prefix}.children.max_age`]) || 26,
		can_children_has_max_benefit_amount_than_employee:
			!!fd[`${prefix}.children.can_children_has_max_benefit_amount_than_employee`],
		disabled: {
			min_age: Number(fd[`${prefix}.disabled.min_age`]) || 0,
			max_age: Number(fd[`${prefix}.disabled.max_age`]) || 100,
			children_can_exceed_employee_benefit: !!fd[`${prefix}.disabled.children_can_exceed_employee_benefit`]
		}
	}
});

/** The rider sub-document. Mirrors CreateVEBPlanV2's rider payload. */
const buildRider = (fd: Fd) => ({
	is_present: fd.is_this_plan_has_riders || false,
	is_visible_in_form: true,
	beneficiaries_required: fd["rider.Beneficiaries"] || false,

	// ── Basic info ──
	name: formatToTitleCase(fd["rider.name"]) || "",
	is_rider_only_for_employee: fd["rider.is_rider_only_for_employee"] || false,
	is_required: fd["rider.is_required"] || false,
	is_rider_has_same_base_plan_coverage_for: fd["rider.is_rider_has_same_base_plan_coverage_for"] || false,

	// ── Coverage ──
	can_each_relationship_enroll_indipendently_in_this_plan: fd["rider.indenpendent_relationship"] || false,
	employee_required: fd["rider.employee_require"] || false,

	coverage_for: {
		options: coverageSort(
			fd["rider.is_rider_only_for_employee"]
				? ["Employee Only"]
				: fd["rider.is_rider_has_same_base_plan_coverage_for"]
				? fd.coverage_for
				: fd["rider.coverage_for"] || []
		),
		is_present: true,
		is_visible_in_form: true
	},

	coverage_volume: {
		options: fd["rider.coverage_volume_present"]
			? (fd["rider.coverage_volume"] || []).map((item: string) => formatToTitleCase(item))
			: [],
		is_present: fd["rider.coverage_volume_present"] || false,
		is_visible_in_form: true
	},

	coverage_volume_same_as_employee: fd["rider.coverage_volume_same_as_employee"] || false,

	pay_frequency: {
		options: fd["rider.pay_frequency"] || [],
		is_present: true,
		is_visible_in_form: true
	},

	is_premium_depends_on_smoke: fd["rider.is_premium_depends_on_smoke"] || false,
	is_premium_depnends_on_spouse_smoke: fd["rider.benefit_amount.spouse.is_premium_depends_on_smoke"] || false,
	is_premium_depnends_on_child_smoke: fd["rider.benefit_amount.child.is_premium_depends_on_smoke"] || false,

	benefit_amount: buildBenefitAmount(fd, "rider.benefit_amount", "rider.is_benefit"),

	is_benefit_dependent_on_paycheck:
		fd["rider.is_benefit"] && fd["rider.is_benefit_dependent_on_paycheck"] ? true : false,

	...buildIssueBlock(fd, "rider"),

	age_criteria: buildPlanAgeCriteria(fd, "rider.age_criteria", "rider.is_this_plan_has_age_criteria"),

	employee_eligibility: {
		is_visible_in_form: false,
		employee: {
			min_age: fd["rider.employee_eligibility.min_age"] || 16,
			max_age: fd["rider.employee_eligibility.max_age"] || 100
		}
	},

	isriderdependent_eligibility: fd["rider.isdependent_eligibility"] || false,

	dependent_eligibility: buildDependentEligibility(fd, "rider.dependent_eligibility", "rider.isdependent_eligibility")
});

export interface BuildCorePlanPayloadOptions {
	group_number: number;
	/** Parsed premium workbook, or null before the Excel step. */
	premiumAmount?: any;
	/** Current wizard step, echoed back like the VEB payload does. */
	step?: number;
}

/**
 * Build the full Core plan payload from the CoreConfig form state.
 * Always sets `same_as_veb_structure: true` — this builder is only used on that path.
 */
export const buildCorePlanPayload = (formData: Fd, opts: BuildCorePlanPayloadOptions) => {
	const fd = formData;

	const normalizedPayFrequency = (fd.pay_frequency || []).map((pf: string) => String(pf).toUpperCase());

	// Core-only schema field with no form field of its own: the plan applies to
	// dependents exactly when dependents are marked eligible in step 3.
	const isDependentApplied = !!fd.isdependent_eligibility;

	// The "pay-frequency-amounts" field already produces [{ pay_frequency, min_limit }].
	const customContributions = fd.has_employee_custom_contribution
		? fd.custom_contribution_of_employees || []
		: [];

	return {
		// ── Core identity ──
		group_number: opts.group_number,
		same_as_veb_structure: true,

		plan_name: formatToTitleCase(fd.plan_name),
		plan_code: trimSpaces(fd.plan_code),
		// Not collected from the user on Core plans — always sent empty.
		carrier_name: "",
		plan_order: Number(fd.plan_order),
		plan_year: fd.plan_year,
		status: fd.status,
		deduction_code: fd["deduction_code"] || null,
		// Core stores employee_class as an array (and has no "ALL" option).
		employee_class: Array.isArray(fd.employee_class) ? fd.employee_class : fd.employee_class ? [fd.employee_class] : [],
		start_date: fd.start_date,
		is_end_date_required: fd.is_end_date_required || false,
		end_date: fd.is_end_date_required ? fd.end_date || null : null,

		// ── Core-only IPlanSchema flags ──
		auto_approve: fd.auto_approve || false,
		beneficiaries_required: fd.Beneficiaries || false,
		has_premium: fd.has_premium || false,
		has_employer_contribution: fd.has_employer_contribution || false,
		has_waive_approval: fd.has_waive_approval || false,
		is_always_open: fd.is_always_open || false,
		is_dependent_applied: isDependentApplied,
		// Boolean, matching what the existing Core form sends (its own Plan type
		// declares it boolean, even though IPlanSchema types it as string).
		buy_up_eligibility: fd.buy_up_eligibility || false,
		has_employee_custom_contribution: fd.has_employee_custom_contribution || false,
		custom_contribution_of_employees: customContributions,
		plan_dependencies: fd.standard_plan_dependency || [],
		// Always empty: a VEB-structured plan expresses dependencies at plan level
		// (standard_plan_dependency / rider_plan_dependency), not as sub-plans.
		internal_dependencies: [],

		document: null,

		// ── VEB-shaped structure ──
		is_premium_depends_on_smoke: fd.is_premium_depends_on_smoke || false,
		is_premium_depnends_on_spouse_smoke: fd["benefit_amount.spouse.is_premium_depends_on_smoke"] || false,
		is_premium_depnends_on_child_smoke: fd["benefit_amount.child.is_premium_depends_on_smoke"] || false,

		can_each_relationship_enroll_indipendently_in_this_plan: fd["indenpendent_relationship"] || false,
		employee_required: fd["employee_require"] || false,

		coverage_for: {
			options: coverageSort(fd.coverage_for || []),
			is_present: true,
			is_visible_in_form: true
		},

		coverage_volume: {
			options: fd.coverage_volume_present
				? (fd.coverage_volume || []).map((item: string) => formatToTitleCase(item))
				: [],
			is_present: fd.coverage_volume_present || false,
			is_visible_in_form: true
		},

		pay_frequency: {
			options: normalizedPayFrequency,
			is_present: true,
			is_visible_in_form: true
		},

		premium_amount: opts.premiumAmount ?? null,

		benefit_amount: buildBenefitAmount(fd, "benefit_amount", "is_benefit"),

		is_benefit_dependent_on_paycheck: fd.is_benefit && fd.is_benefit_dependent_on_paycheck ? true : false,

		...buildIssueBlock(fd, ""),

		age_criteria: buildPlanAgeCriteria(fd, "age_criteria", "is_this_plan_has_age_criteria"),

		employee_eligibility: {
			is_visible_in_form: false,
			employee: {
				min_age: fd["employee_eligibility.min_age"] || 16,
				max_age: fd["employee_eligibility.max_age"] || 100
			}
		},

		dependent_eligibility: buildDependentEligibility(fd, "dependent_eligibility", "isdependent_eligibility"),

		is_this_plan_has_riders: !!fd.is_this_plan_has_riders,
		rider: buildRider(fd),

		step: opts.step,
		rider_plan_dependency: fd["rider_plan_dependency"],
		standard_plan_dependency: fd["standard_plan_dependency"],
		has_general_question: fd["has_questions"] || false,
		general_questions: fd["questions"] || []
	};
};

/**
 * Reduce a built payload to the subset `jsonToExcel` needs to generate the
 * premium workbook template. Mirrors CreateVEBPlanV2's `excelPayload`.
 */
export const buildCoreExcelPayload = (payload: ReturnType<typeof buildCorePlanPayload>, formData: Fd) => ({
	// Despite the name this is jsonToExcel's sheet selector, not the plan's
	// carrier — VEB likewise puts the plan name here. It must stay is_present:true
	// or the workbook's switch key won't match and no sheet is generated.
	carrier_name: {
		value: payload.plan_name,
		type: "string",
		is_present: true,
		is_visible_in_form: true
	},
	auto_approve: payload.auto_approve,
	is_premium_depends_on_smoke: formData["is_premium_depends_on_smoke"] || false,
	is_premium_depnends_on_spouse_smoke: payload.is_premium_depnends_on_spouse_smoke,
	is_premium_depnends_on_child_smoke: payload.is_premium_depnends_on_child_smoke,
	coverage_for: payload.coverage_for,
	coverage_volume: payload.coverage_volume,
	pay_frequency: payload.pay_frequency,
	premium_amount: payload.premium_amount,
	benefit_amount: payload.benefit_amount,
	is_this_plan_has_age_criteria: payload.age_criteria.is_present,
	age_criteria: payload.age_criteria,
	dependent_eligibility: payload.dependent_eligibility,
	is_this_plan_has_riders: payload.is_this_plan_has_riders,
	can_each_relationship_enroll_indipendently_in_this_plan:
		payload.can_each_relationship_enroll_indipendently_in_this_plan || false,
	rider: payload.rider
});
